import { type ReactNode } from 'react';
import { ContentStatsCard, type ContentStatsCardProps } from './cards/ContentStatsCard';
import { ContentQualityCard, type ContentQualityCardProps } from './cards/ContentQualityCard';
import { TrustSignalsCard, type TrustSignalsCardProps } from './cards/TrustSignalsCard';
import { TopicCoverageCard, type TopicCoverageCardProps } from './cards/TopicCoverageCard';
import {
  StructureChecklistCard,
  type StructureChecklistCardProps,
} from './cards/StructureChecklistCard';
import { HeroImageCard, type HeroImageCardProps } from './cards/HeroImageCard';
import {
  ImageMetadataCard,
  type ImageMetadataCardProps,
} from './cards/ImageMetadataCard';
// ImageSuggestionsCard import retained for type only — card is no longer rendered
// (hidden to prevent image-credit exhaustion).
import { type ImageSuggestionsCardProps } from './cards/ImageSuggestionsCard';
import { SerpPreviewCard, type SerpPreviewCardProps } from './cards/SerpPreviewCard';
import { SummaryCard, type SummaryCardProps } from './cards/SummaryCard';
import {
  StructuredDataCard,
  type StructuredDataCardProps,
} from './cards/StructuredDataCard';
import {
  SocialMetadataCard,
  type SocialMetadataCardProps,
} from './cards/SocialMetadataCard';
import {
  PublishingSettingsCard,
  type PublishingSettingsCardProps,
} from './cards/PublishingSettingsCard';
import { InternalLinksCard, type InternalLinksCardProps } from './cards/InternalLinksCard';
import { ExternalLinksCard, type ExternalLinksCardProps } from './cards/ExternalLinksCard';

// WordPressCard import retained for type only — card is no longer rendered
// (duplicates the top toolbar's preview/publish controls).
import { type WordPressCardProps } from './cards/WordPressCard';
// FocusKeywordCard import retained for type only — the focus keyword now lives
// next to the title in the top bar (WordPress-like), not in the rail.
import { type FocusKeywordCardProps } from './cards/FocusKeywordCard';
import {
  SocialRepurposeCard,
  type SocialRepurposeCardProps,
} from './cards/SocialRepurposeCard';
import { SuggestionsCard, type SuggestionsCardProps } from './cards/SuggestionsCard';
import { RailFilterSlot } from './railFilter';

export interface ToolsTabProps {
  /** First-row "Inline suggestions" toggle + list. */
  suggestions: SuggestionsCardProps;
  /** ContentStats — words, headings, focus terms, status. */
  stats: ContentStatsCardProps;
  /** Quality gauge + 4 collapsible sections; null = no analysis yet (renders an honest empty state instead of demo data). */
  quality: ContentQualityCardProps | null;
  /** Reality + Authority + Temporal cluster. */
  trust: TrustSignalsCardProps;
  /** Keyword coverage chips. */
  coverage: TopicCoverageCardProps;
  /** H1/H2/H3 hierarchy checklist. */
  structure: StructureChecklistCardProps;
  /** Hero image preview / upload / generate. */
  hero: HeroImageCardProps;
  /** Family D — per-image alt/caption metadata list (rendered after the hero image). */
  imageMetadata: ImageMetadataCardProps;
  /**
   * "Analyze for images" → list of placement suggestions.
   * Optional + no longer rendered — kept to avoid breaking callers.
   * (Image opportunities card hidden to prevent credit exhaustion.)
   */
  imageSuggestions?: ImageSuggestionsCardProps;
  /** SERP preview + meta editor with live counters. */
  serp: SerpPreviewCardProps;
  /** Family C — excerpt editor + read-only key takeaways (rendered after the SERP preview). */
  summary: SummaryCardProps;
  /**
   * Optional banner rendered above the SERP preview card. Holds the
   * whole-run Undo affordance and the per-field revert list (Session C) for
   * metadata runs triggered from the structured-data drawer / MetadataFixRow.
   * Additive optional prop — null/undefined renders nothing. (The bulk
   * "Regenerate metadata" rail card that used to sit above this banner was
   * removed — per-field AiSuggestButton + the drawer's generate action cover
   * the same job without duplicating it.)
   */
  metadataRunBanner?: ReactNode;
  /** Family E — slim structured-data summary + Inspect-graph button (opens the drawer). */
  structuredData: StructuredDataCardProps;
  /** Social metadata (OG + Twitter overrides). */
  socialMetadata: SocialMetadataCardProps;
  /** Publishing settings (canonical, indexing, author). */
  publishingSettings: PublishingSettingsCardProps;
  /** Internal-link list — all internal client links used in this blog. */
  internalLinks: InternalLinksCardProps;
  /** External-link list — all external links used in this blog. */
  externalLinks: ExternalLinksCardProps;
  /**
   * WordPress publish status.
   * Optional + no longer rendered — kept to avoid breaking callers.
   * (WordPress card removed from rail — duplicates the top publishing controls.)
   */
  wordPress?: WordPressCardProps;
  /** Social repurpose generator. */
  social: SocialRepurposeCardProps;
  /** Focus keyword, surfaced as a card at the bottom of the rail. */
  focusKeyword: FocusKeywordCardProps;
  /** Optional extra cards rendered at the bottom. */
  trailing?: ReactNode;
}

/** Empty state shown while no quality report exists (e.g. generation pending/failed). */
function QualityNotAnalyzedCard() {
  return (
    <div className="rounded-lg border bg-card p-4">
      <p className="text-sm font-medium">Content quality</p>
      <p className="mt-1 text-xs text-muted-foreground">
        Not analyzed yet — scores appear after generation completes and the
        quality report runs.
      </p>
    </div>
  );
}

/**
 * Tools tab — vertical card stack in the exact order specified by the v3
 * design package (chat1.md lines 706-906 + editor-rail.jsx). All cards are
 * fully prop-driven; the parent owns data and callbacks.
 *
 * Each card sits in a RailFilterSlot naming the mini-rail group(s) it belongs
 * to; with no filter active the whole stack renders exactly as before. Slots
 * with `under={[]}` (Suggestions, trailing) have no mini-rail home and appear
 * only in the unfiltered view. Cards stay mounted while filtered out.
 */
export function ToolsTab(props: ToolsTabProps) {
  return (
    <>
      <RailFilterSlot under={[]}>
        <SuggestionsCard {...props.suggestions} />
      </RailFilterSlot>
      <RailFilterSlot under={['score']}>
        <ContentStatsCard {...props.stats} />
      </RailFilterSlot>
      <RailFilterSlot under={['quality']}>
        {props.quality ? (
          <ContentQualityCard {...props.quality} />
        ) : (
          <QualityNotAnalyzedCard />
        )}
      </RailFilterSlot>
      <RailFilterSlot under={['quality']}>
        <TrustSignalsCard {...props.trust} />
      </RailFilterSlot>
      <RailFilterSlot under={['structure']}>
        <StructureChecklistCard {...props.structure} />
      </RailFilterSlot>
      <RailFilterSlot under={['images']}>
        <HeroImageCard {...props.hero} />
      </RailFilterSlot>
      <RailFilterSlot under={['images']}>
        <ImageMetadataCard {...props.imageMetadata} />
      </RailFilterSlot>
      {/* ImageSuggestionsCard hidden — prevents image-credit exhaustion (re-enable later) */}
      {/* Bulk "Regenerate metadata" card removed from the rail — per-field
          AiSuggestButton on each metadata card + the structured-data drawer's
          generate action cover the same job. handleGenerateMetadata/
          blogCreationService.regenerateMetadata stay wired to those. */}
      {props.metadataRunBanner != null && (
        <RailFilterSlot under={['meta']}>{props.metadataRunBanner}</RailFilterSlot>
      )}
      <RailFilterSlot under={['meta']}>
        <SerpPreviewCard {...props.serp} />
      </RailFilterSlot>
      <RailFilterSlot under={['meta']}>
        <SummaryCard {...props.summary} />
      </RailFilterSlot>
      <RailFilterSlot under={['social']}>
        <SocialMetadataCard {...props.socialMetadata} />
      </RailFilterSlot>
      <RailFilterSlot under={['meta']}>
        <StructuredDataCard {...props.structuredData} />
      </RailFilterSlot>
      <RailFilterSlot under={['meta']}>
        <PublishingSettingsCard {...props.publishingSettings} />
      </RailFilterSlot>
      <RailFilterSlot under={['links']}>
        <InternalLinksCard {...props.internalLinks} />
      </RailFilterSlot>
      <RailFilterSlot under={['links']}>
        <ExternalLinksCard {...props.externalLinks} />
      </RailFilterSlot>
      {/* WordPress card removed from rail — duplicates the top publishing controls
          (the only publish control lives in the top DocChrome/TopBar). */}
      <RailFilterSlot under={['social']}>
        <SocialRepurposeCard {...props.social} />
      </RailFilterSlot>
      {/* A1: Keywords/Tags (Topic coverage) moved to the BOTTOM for a cleaner
          top-down flow — was previously rendered near the top. */}
      <RailFilterSlot under={['score']}>
        <TopicCoverageCard {...props.coverage} />
      </RailFilterSlot>
      {/* Focus keyword moved to the top bar (next to the title); no longer
          rendered in the rail. props.focusKeyword retained for compatibility. */}
      {props.trailing != null && (
        <RailFilterSlot under={[]}>{props.trailing}</RailFilterSlot>
      )}
    </>
  );
}
